import { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  View,
  Button,
} from "@/components/ui";
import { OTP } from "@/components/shared/otp";
import { useCountdown } from "@/components/hooks/use-count-down";
import { ErrorMessage } from "@/components/shared/login-register/error-message";
import { useProfileState } from "@/store/profile";
import { ChangePhoneNumberModalEnum } from "@/types/profile";
import { useClientConfirmPhoneNumberStore } from "@/store/auth/clientConfirmPhoneNumber";
import { Typography } from "@/components/shared/typography";
import { useAuthState } from "@/store/auth";
import { useClientStore } from "@/store/auth/client";
import { useUpdateAccountPhoneNumberStore } from "@/store/auth/updateAccountPhoneNumber";

export function ChangeNumberOtpVerificationModal({ open }: { open: boolean }) {
  const {
    data: { userInfo },
  } = useAuthState();
  const { count, time, resetCount } = useCountdown(59);
  const {
    showModal,
    setPinCode,
    data: { pinCode, updatedPhone },
  } = useProfileState();

  useEffect(() => {
    if (open) {
      resetCount();
      setPinCode("");
    }
  }, [open]);

  const {
    fetch: clientConfirmPhoneNumber,
    error: clientConfirmPhoneNumberError,
    loading: clientConfirmPhoneNumberLoading,
  } = useClientConfirmPhoneNumberStore();

  const { fetch: client, loading: clientLoading } = useClientStore();

  const {
    fetch: updateAccountPhoneNumber,
    loading: updateAccountPhoneNumberLoading,
    error: updateAccountPhoneNumberError,
  } = useUpdateAccountPhoneNumberStore();

  const loading =
    clientConfirmPhoneNumberLoading ||
    clientLoading ||
    updateAccountPhoneNumberLoading;

  const handleContinue = async () => {
    try {
      // First, confirm the OTP
      const result = await clientConfirmPhoneNumber({
        requestPayload: {
          input: {
            phone: updatedPhone,
            onSignUp: true,
            code: pinCode,
          },
        },
      });

      if (result.error) {
        // OTP verification failed, don't proceed
        return;
      }

      // Then, update the phone number after successful OTP verification
      const updateResult = await updateAccountPhoneNumber({
        requestPayload: {
          input: {
            clientId: userInfo.id,
            phoneNumber: updatedPhone,
          },
        },
      });

      if (updateResult.data?.success) {
        // Refresh client data
        await client({
          requestPayload: {
            id: userInfo.id,
          },
        });

        showModal(ChangePhoneNumberModalEnum.CHANGE_PHONE_NUMBER_SUCCESS);
      }
    } catch (error) {
      console.error("Failed to update phone number:", error);
    }
  };

  const handleCancel = () => {
    showModal(ChangePhoneNumberModalEnum.CANCEL_CHANGE_PHONE_NUMBER);
  };

  return (
    <Dialog open={open}>
      <DialogContent className="w-[343px]">
        <DialogHeader className="flex flex-col gap-1">
          <DialogTitle>OTP Verification</DialogTitle>
          <DialogDescription>
            {`A verification phone number has been sent to (${updatedPhone}).`}
          </DialogDescription>
        </DialogHeader>
        <View className="flex flex-col items-stretch">
          <View className="mb-4 mt-2">
            <OTP pinCode={pinCode} onOTPChange={setPinCode} />
          </View>

          <View className="flex flex-col gap-2">
            <Button
              variant="primary"
              color="CTA"
              loading={loading}
              disabled={count === 0 || pinCode.length !== 6}
              onClick={handleContinue}
              children="Continue"
            />
            <Button
              variant="secondary"
              color="CTA2"
              loading={loading}
              onClick={handleCancel}
              children="Cancel"
            />
          </View>
          <Typography
            variant="body-sm"
            color="secondary-text"
            align="center"
            className="pb-2 pt-4"
          >
            {`Remaining time: ${time}s`}
          </Typography>
        </View>
        {(clientConfirmPhoneNumberError || updateAccountPhoneNumberError) && (
          <ErrorMessage
            errorMessage={
              clientConfirmPhoneNumberError || updateAccountPhoneNumberError
            }
            className="mt-2"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
